import {
  getCardPresentation,
  getCardUi,
  type CardStatusMetadata,
  type CardStatusTone,
  type GameCardUiContext,
  type PublicVisibleEffectView,
} from '@cards/cards';
import type { StatMod } from '@cards/engine';
import { pixelIconForSymbol, type PixelIconName } from './PixelIcon.js';

export type { CardStatusTone } from '@cards/cards';

/**
 * Status families (design system 04 §2.1). The family sets a label's colour,
 * the icon names the status, so the family is read off the icon; a status the
 * table does not know keeps its authored tone. `warning` doubles as Tempo
 * (Rush, gold), `positive`/`negative` as Boost/Weakening.
 */
export type CardStatusFamily =
  | 'heat' | 'freeze' | 'defense' | 'positive' | 'negative' | 'warning' | 'limit' | 'rival' | 'used' | 'neutral';

/**
 * The colours of each status family (04 §2.1): the one table both the board's
 * status plates (CSS in surfaces.css.ts) and the large card's status rows (the
 * row's left edge, BoardCard) read, so a family is the same colour everywhere.
 * Values are CSS colour expressions over the role variables. The slate
 * families (limit, used, neutral) sit on panel-edge — one step lighter than a
 * card's dark face (look A), on which panel-raised vanished.
 */
export const STATUS_FAMILY_TONES: Readonly<Record<CardStatusFamily, { readonly face: string; readonly ink: string }>> = {
  heat: { face: 'var(--c-heat)', ink: 'var(--c-text-on-card)' },
  freeze: { face: 'var(--c-freeze)', ink: 'var(--c-text-on-card)' },
  defense: { face: 'var(--c-health)', ink: 'var(--c-text-on-color)' },
  positive: { face: 'var(--c-positive)', ink: 'var(--c-text-on-card)' },
  negative: { face: 'var(--c-negative)', ink: 'var(--c-text-on-color)' },
  warning: { face: 'var(--c-energy)', ink: 'var(--c-text-on-energy)' },
  rival: { face: 'var(--c-rival)', ink: 'var(--c-text-on-card)' },
  limit: { face: 'var(--c-panel-edge)', ink: 'var(--c-text-on-dark)' },
  used: { face: 'color-mix(in srgb, var(--c-panel-edge) 60%, transparent)', ink: 'var(--c-text-muted-on-dark)' },
  neutral: { face: 'var(--c-panel-edge)', ink: 'var(--c-text-on-dark)' },
};

const ICON_FAMILIES: Partial<Record<PixelIconName, CardStatusFamily>> = {
  heat: 'heat', gear: 'heat',
  freeze: 'freeze',
  shield: 'defense', barrier: 'defense',
  rush: 'warning',
  buff: 'positive', growth: 'positive', sunflower: 'positive', attack: 'positive', fullLocation: 'positive',
  bloom: 'positive', sun: 'positive',
  debuff: 'negative', timedDestroy: 'negative', damaged: 'negative',
  delay: 'limit', prohibited: 'limit', net: 'limit',
  hack: 'rival', controller: 'rival', capture: 'rival', attachment: 'rival',
  check: 'used', bounce: 'used', spotlight: 'used', support: 'used', soften: 'used', copy: 'used',
};

export function cardStatusFamily(status: {
  readonly icon?: PixelIconName;
  readonly symbol?: string;
  readonly tone: CardStatusTone;
}): CardStatusFamily {
  const symbol = status.symbol ?? '';
  const icon = status.icon ?? pixelIconForSymbol(symbol) ?? pixelIconForSymbol([...symbol][0] ?? '');
  // «Стянуться в ком» is a boost while it adds attack and a limit while it forbids it.
  if (icon === 'clot') return status.tone === 'negative' ? 'limit' : 'positive';
  return (icon === undefined ? undefined : ICON_FAMILIES[icon]) ?? status.tone;
}

/**
 * A card status enriched with an optional pixel glyph. Statuses produced here
 * set `icon` (rendered before any `symbol` text); statuses contributed by card
 * UI providers arrive as plain `CardStatusMetadata` and fall back to `symbol`.
 */
export interface CardStatusView extends CardStatusMetadata {
  readonly icon?: PixelIconName;
}

function cardName(definitionId: string): string {
  return getCardPresentation(definitionId)?.name ?? definitionId;
}

function roundDuration(effect: PublicVisibleEffectView): string {
  if (effect.duration !== undefined) return `Осталось раундов: ${effect.duration}.`;
  if (effect.persistent) return 'Постоянный эффект.';
  return effect.sourceDefinitionId === undefined
    ? 'Действует без ограничения по времени.'
    : 'Действует, пока активен источник.';
}

function sourceDescription(effect: PublicVisibleEffectView): string {
  return effect.sourceDefinitionId === undefined ? '' : ` Источник: ${cardName(effect.sourceDefinitionId)}.`;
}

function modifierLabel(modifier: StatMod): string {
  const stat = modifier.stat === 'attack' ? 'к атаке' : 'к максимальному здоровью';
  const value = modifier.op === 'add'
    ? `${modifier.value >= 0 ? '+' : ''}${modifier.value}`
    : modifier.op === 'mul'
      ? `×${modifier.value}`
      : `=${modifier.value}`;
  return `${value} ${stat}`;
}

function negativeModifier(modifier: StatMod): boolean {
  return (modifier.op === 'add' && modifier.value < 0)
    || (modifier.op === 'mul' && modifier.value < 1);
}

function stackLabel(label: string, count: number): string {
  return count === 1 ? label : `${label} ×${count}`;
}

function modifierShape(effect: PublicVisibleEffectView): string {
  return effect.statMods
    .map((modifier) => `${modifier.stat}:${modifier.op}:${modifier.op === 'set' ? modifier.value : '*'}`)
    .sort()
    .join('|');
}

function effectGroupKey(effect: PublicVisibleEffectView): string {
  // Player effects deliberately keep their current one-row-per-effect behavior.
  if (effect.scopeKind === 'player') return effect.key;
  return JSON.stringify([
    effect.scopeKind,
    effect.definitionId,
    effect.sourceDefinitionId ?? null,
    effect.duration ?? null,
    effect.persistent,
    effect.shield === undefined ? ['no-shield'] : ['shield', effect.shield.onDepletedCool ?? null],
    modifierShape(effect),
  ]);
}

function groupEffects(
  effects: readonly PublicVisibleEffectView[],
): readonly (readonly PublicVisibleEffectView[])[] {
  const groups = new Map<string, PublicVisibleEffectView[]>();
  for (const effect of effects) {
    const key = effectGroupKey(effect);
    const group = groups.get(key);
    if (group === undefined) groups.set(key, [effect]);
    else group.push(effect);
  }
  return [...groups.values()];
}

function aggregateModifiers(effects: readonly PublicVisibleEffectView[]): StatMod[] {
  const aggregated = new Map<string, StatMod>();
  for (const modifier of effects.flatMap(({ statMods }) => statMods)) {
    const key = `${modifier.stat}:${modifier.op}`;
    const current = aggregated.get(key);
    if (current === undefined) {
      aggregated.set(key, { ...modifier });
      continue;
    }
    if (modifier.op === 'add' && current.op === 'add') {
      aggregated.set(key, { ...current, value: current.value + modifier.value });
    } else if (modifier.op === 'mul' && current.op === 'mul') {
      aggregated.set(key, { ...current, value: current.value * modifier.value });
    }
    // `set` effects only share a group when their value is identical.
  }
  return [...aggregated.values()];
}

function effectGroupStatuses(
  context: GameCardUiContext,
  effects: readonly PublicVisibleEffectView[],
): CardStatusView[] {
  const effect = effects[0];
  if (effect === undefined) return [];
  const providerStatuses = (getCardUi(effect.definitionId)?.effectStatusProviders ?? [])
    .flatMap((provider) => provider(context, effects))
    .map((status) => ({ ...status, id: `${effect.key}-${status.id}` }));
  if (providerStatuses.length > 0) {
    return providerStatuses;
  }

  const count = effects.length;
  const label = stackLabel(cardName(effect.definitionId), count);
  const description: string[] = [];
  const shield = effects.reduce((total, current) => total + (current.shield?.remaining ?? 0), 0);
  if (shield > 0) {
    description.push(`Щит этого источника: ${shield}.`);
    if (effect.shield?.onDepletedCool !== undefined) {
      description.push(count === 1
        ? `После полного расходования охладит карту на ${effect.shield.onDepletedCool}.`
        : `Каждый полностью израсходованный Щит охладит карту на ${effect.shield.onDepletedCool}.`);
    }
  }
  const modifiers = aggregateModifiers(effects);
  if (modifiers.length > 0) {
    description.push(`${modifiers.map(modifierLabel).join(', ')}.`, roundDuration(effect));
  }
  if (shield === 0 && modifiers.length === 0) {
    description.push('На карту действует активный эффект.', roundDuration(effect));
  }
  const source = sourceDescription(effect);
  if (source !== '') description.push(source.trimStart());
  const negative = modifiers.some(negativeModifier);
  return [{
    id: effect.key,
    ...(shield > 0
      ? { icon: 'shield' as const, symbol: `${shield}` }
      : modifiers.length > 0
        ? { icon: (negative ? 'debuff' : 'buff') as PixelIconName, symbol: '' }
        : { icon: 'effect' as const, symbol: '' }),
    label,
    description: description.join(' '),
    tone: negative ? 'negative' : shield > 0 || modifiers.length > 0 ? 'positive' : 'neutral',
    ...(shield > 0 || modifiers.length === 0 ? { compact: false } : {}),
  }];
}

/** Generic renderer input: standard public state plus card-owned providers. */
export function cardStatusViews(context: GameCardUiContext): CardStatusView[] {
  const statuses = groupEffects(context.visibleEffects)
    .flatMap((effects) => effectGroupStatuses(context, effects));
  if (context.timedDestroy !== undefined) {
    statuses.push({
      id: context.timedDestroy.key,
      icon: 'timedDestroy',
      symbol: context.timedDestroy.remainingRounds === 0 ? '!' : `${context.timedDestroy.remainingRounds}`,
      label: 'Отложенное уничтожение',
      description: context.timedDestroy.remainingRounds === 0
        ? 'Карта будет уничтожена в конце этого раунда.'
        : `Карта будет уничтожена в конце раунда ${context.timedDestroy.deadlineRound}.`,
      tone: 'negative',
    });
  }
  if (context.capture !== undefined) {
    statuses.push({
      id: 'capture',
      icon: 'capture',
      symbol: '',
      label: 'Временный захват',
      description: `Вернётся в исходную ячейку в конце раунда ${context.capture.expiresRound}.${context.capture.returnFreeze > 0 ? ` После возврата получит Заморозку ${context.capture.returnFreeze}.` : ''}`,
      tone: 'warning',
      compact: false,
    });
  }
  if (context.viewer.wasDamagedByCard) {
    statuses.push({
      id: 'damaged-viewer',
      icon: 'damaged',
      symbol: '',
      label: 'Наносила вам урон',
      description: 'Эта карта выполняет условие «Корневого доступа» для вас.',
      tone: 'negative',
    });
  }
  for (const provider of getCardUi(context.card.definitionId)?.statusProviders ?? []) {
    statuses.push(...provider(context));
  }
  return statuses;
}
